import { getMinDate, getMaxDate } from "../../utils"
import { addWeeks, isBefore } from "date-fns"
import { RootNode } from "../types"
import { TaskViewModel } from "../../tasks/api/task"
import { TimeZoneType } from "../../constants/timezones"
import { makeTreeGridTask } from "./tree-grid-task"
import { getHighestLevelTask, syncDataFromServerToGrid } from "../utils/tree-grid"
import { AddedRow } from "../hooks/use-added-rows"
import { TreeGridTranslations } from "../hooks/use-translations"

export const getRowById = (rows: any[], id: string) => {
  return rows.find((row) => row.id === id)
}

const DEFAULT_VISIBLE_WEEKS = 4

export const getTaskTreeGridGanttDates = (
  root: Pick<RootNode, "plannedStartDate" | "plannedEndDate">,
  tasks: Pick<
    TaskViewModel,
    "plannedStartDate" | "plannedEndDate" | "actualStartDate" | "actualEndDate" | "isInProgress"
  >[]
) => {
  const now = new Date()

  const taskDates = tasks.flatMap((task) => {
    const dates = [task.plannedStartDate, task.plannedEndDate, task.actualStartDate, task.actualEndDate].filter(Boolean)
    return dates.map((date) => new Date(date as string | number))
  })

  const allDates = [root.plannedStartDate, root.plannedEndDate]
    .filter(Boolean)
    .map((date) => new Date(date as number))
    .concat(taskDates)

  const minStartDate = getMinDate(allDates) ?? now
  const maxDateCandidates = allDates.concat(tasks.some((task) => task.isInProgress) ? [now] : [])
  const maxEndDate = getMaxDate(maxDateCandidates) ?? now

  const minimumDefaultEndDate = addWeeks(minStartDate, DEFAULT_VISIBLE_WEEKS)
  const defaultEndDate = isBefore(maxEndDate, minimumDefaultEndDate) ? minimumDefaultEndDate : maxEndDate

  return {
    minStartDate,
    maxEndDate,
    defaultZoomStartDate: minStartDate,
    defaultZoomEndDate: defaultEndDate,
    zoomFitStartDate: minStartDate,
    zoomFitEndDate: maxEndDate,
  }
}

export const syncAddedAndUpdatedTasks = ({
  grid,
  addedTasks,
  updatedTasks,
  dateFormat,
  timeZone,
  translations,
  setAddedRows,
}: SyncAddedAndUpdatedTasksParams) => {
  if (!grid) return { addedRows: [], updatedRows: [] }
  const addedRows = addedTasks.map((task) => makeTreeGridTask({ task, dateFormat, timeZone, translations }))
  addedRows.forEach((addedRow) => {
    const parentRow = addedRow.parentTaskId ? grid.GetRowById(addedRow.parentTaskId) : undefined
    let nextRow = addedRow.parentTaskId ? parentRow?.firstChild : grid.GetFirst()
    while (nextRow) {
      const nextRowOrder = (nextRow as unknown as { order: number }).order
      const addedRowOrder = addedRow.order
      if (nextRowOrder === addedRowOrder) break
      nextRow = nextRow.nextSibling
    }
    grid.AddRow(parentRow, nextRow, 1, addedRow.id)
  })

  const updatedRows = updatedTasks.map((task) => makeTreeGridTask({ task, dateFormat, timeZone, translations }))
  syncDataFromServerToGrid(grid, [...addedRows, ...updatedRows])
  const highestLevelTask = getHighestLevelTask(addedTasks)
  setAddedRows(
    addedTasks.map((task) => ({
      ...task,
      focus: task.id === highestLevelTask.id,
      scrollTo: task.id === highestLevelTask.id,
    }))
  )

  return {
    addedRows,
    updatedRows,
  }
}

type SyncAddedAndUpdatedTasksParams = {
  grid: TGrid
  addedTasks: TaskViewModel[]
  updatedTasks: TaskViewModel[]
  dateFormat: string
  timeZone: TimeZoneType
  translations: TreeGridTranslations
  setAddedRows: (rows: AddedRow[]) => void
}
